import { DataSourceMode, Textbox } from "@mcleod/components";
import { getThemeColor } from "@mcleod/core";
import { DuplicateIdProps } from "../DuplicateId";

const VIEW_MC_CONTROL_URL = "lme/dispatch/mobilecomm/MobileCommControl?mode=update&key=";

export const VALIDATION_CONFIG = {
    VENDOR_ID_MAX_LENGTH: 10,
    VENDOR_ID_MIN_LENGTH: 3,
    TOOLTIP_TIMEOUT: 5000,
    DUPLICATE_VALIDATION_PROPS: {
        tableName: "mc_control",
        screenName: "Mobile Comm Control",
        navigateToUrl: VIEW_MC_CONTROL_URL
    } as DuplicateIdProps,
    DIALOG_PROPS: {
        title: "Invalid Vendor",
        xVisible: false,
        titleProps: {
            color: "default.reverse"
        },
        panelTitleProps: {
            paddingTop: 10,
            paddingBottom: 10,
            backgroundColor: "primary.darker"
        },
        okProps: {
            maxWidth: 100,
            backgroundColor: getThemeColor("primary"),
            color: getThemeColor("primary.reverse")
        }
    }
} as const;

export const HOS_VENDORS: readonly string[] = [
    "BLU", "CAR", "DRV", "ERO", "FLE", "FTS", "GEO",
    "IBR", "ERO", "DRI", "COR", "IGL", "ITR", "JJK", "LBT",
    "LIN", "LYT", "MAV", "MCL", "OMN", "ORB", "PAR",
    "PED", "PEG", "PEO", "PLA", "PNE", "QCH", "QCP",
    "QCW", "RAN", "SAM", "SKY", "SYM", "TEL", "TLR",
    "TND", "TRA", "VER", "VIS", "XRS", "ZON"
] as const;

export const TRACKING_VENDORS: readonly string[] = [
    "BLU", "CAR", "FLE", "GEO", "IBR", "ITR", "JJK", "LBT", "ORB", "PAR",
    "PED", "PLA", "RAN", "SAM", "SKY", "SYM", "TLR", "VER", "VIS"
] as const;

/**
 * Utility class for Mobile Comm Control vendor validation
 */
export class MobileCommValidationUtils {
    /**
     * Creates validation message for invalid vendor IDs
     */
    static createVendorValidationMessage(
        vendorId: string,
        isTrackingEnabled: boolean
    ): string {
        const validVendorSuffixes = isTrackingEnabled
            ? TRACKING_VENDORS.join(', ')
            : HOS_VENDORS.join(', ');

        return `The vendor "${vendorId}" does not start with a valid and known vendor value. ` +
               `\nValid vendor prefixes are: ${validVendorSuffixes}`;
    }

    /**
     * Validates that the vendor ID starts with a known vendor code
     */
    static async validateVendorId(
        textbox: Textbox,
        vendorId: string,
        isTrackingEnabled: boolean,
        updateVisibilityCallback?: (vendorId: string) => void
    ): Promise<boolean> {
        textbox.dataSource.setComponentsBusy(true);
        textbox.validationWarning = null;

        if (textbox.dataSource.mode === DataSourceMode.ADD) {
            // Control MobileCommControlSendLoadListenerTable visibility based on vendorId
            updateVisibilityCallback?.(vendorId);

            const vendorPrefix = vendorId.substring(0, VALIDATION_CONFIG.VENDOR_ID_MIN_LENGTH).toUpperCase();

            if (vendorPrefix.length < VALIDATION_CONFIG.VENDOR_ID_MIN_LENGTH) {
                textbox.validationWarning = `Vendor must be at least ${VALIDATION_CONFIG.VENDOR_ID_MIN_LENGTH} characters long.`;
            } else if (isTrackingEnabled) {
                if (!this.isValidTrackingVendorPrefix(vendorPrefix)) {
                    textbox.validationWarning = this.createVendorValidationMessage(vendorId, true);
                }
            } else {
                if (!this.isValidHOSVendorPrefix(vendorPrefix)) {
                    textbox.validationWarning = this.createVendorValidationMessage(vendorId, false);
                }
            }

            if (textbox.validationWarning) {
                textbox.showAdhocTooltip(null, { timeout: VALIDATION_CONFIG.TOOLTIP_TIMEOUT });
            }
        }

        textbox.dataSource.setComponentsBusy(false);
        return textbox.validationWarning == null;
    }

    /**
     * Validates if a vendor prefix is in the HOS vendors list
     */
    static isValidHOSVendorPrefix(prefix: string): boolean {
        return HOS_VENDORS.includes(prefix);
    }

    /**
     * Validates if a vendor prefix is in the tracking vendors list
     */
    static isValidTrackingVendorPrefix(prefix: string): boolean {
        return TRACKING_VENDORS.includes(prefix);
    }
}
